import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Lightweight modal — no Radix dependency.
 * <Modal open={...} onClose={...}>
 *   <ModalHeader><ModalTitle>...</ModalTitle><ModalDescription>...</ModalDescription></ModalHeader>
 *   <ModalBody>...</ModalBody>
 *   <ModalFooter>...</ModalFooter>
 * </Modal>
 */
function Modal({ open, onClose, children, className }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative z-10 w-full max-w-lg rounded-xl border border-border bg-card text-card-foreground shadow-2xl animate-fade-in',
          className
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

const ModalHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5 p-6 pb-2', className)} {...props} />
);
const ModalTitle = ({ className, ...props }) => (
  <h2 className={cn('text-lg font-semibold leading-none tracking-tight', className)} {...props} />
);
const ModalDescription = ({ className, ...props }) => (
  <p className={cn('text-sm text-muted-foreground', className)} {...props} />
);
const ModalBody = ({ className, ...props }) => (
  <div className={cn('px-6 py-4', className)} {...props} />
);
const ModalFooter = ({ className, ...props }) => (
  <div
    className={cn('flex flex-col-reverse gap-2 p-6 pt-2 sm:flex-row sm:justify-end', className)}
    {...props}
  />
);

export { Modal, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter };
